/**
 * CreditsCenter — the wallet, centred under the top edge. Replaces the header control entirely.
 *
 * PO 2026-09-21, on the first attempt: *"thats terrible. we simply have credits centralised like in
 * the top middle comp0onent of glowtest_FE.. it shows the credits."* — and *"REMOVE THIS fully"*.
 * Both halves were right, and the placement was the whole point:
 *
 *   - the previous version sat in the CRM header's right-hand cluster, as one more outline control
 *     among ThemeToggle / Refresh / UserMenu. A balance is not a tool; putting it there turned the
 *     number into chrome and made it read as "N reachouts left" — a negotiated allowance — where the
 *     user is looking for a wallet.
 *   - glowtest_FE centres it: `#subscriptions-center` in `components/ui/CreditFlag.css` is
 *     `position: fixed; left: 0; right: 0; width: fit-content; margin: 0 auto`, `top: 59px`, with
 *     `border-radius: 0 0 15px 15px` and borders only on the left/bottom/right — a tab that HANGS
 *     from the top edge, centred, visible on every page. This is that shape, rethemed to raisy:
 *     `fixed top-12` (the CRM header is `h-12`) and the same centred `left-0 right-0 w-fit mx-auto`.
 *
 * THE FIGURE IS CREDITS, not reachouts. glowtest shows the balance; the earlier version converted it
 * to "reachouts left on your plan", which is a derived reading the user then has to check. The
 * reachout price belongs where a reachout is actually being spent (the cost preview at the send
 * button), not in a permanent badge that silently re-derives it against whatever tier is loaded.
 *
 * HONEST ZERO, still, and it is the one thing the old version got right: `credits === undefined`
 * means "not read yet" and renders a quiet placeholder — never `0`, because "we do not know" and
 * "you have nothing" are different facts and only one of them should alarm someone.
 *
 * CLICKING IT OPENS THE ONE DIALOG. There is exactly one paywall in this app
 * (`MonetizationProvider`); this is a way in, not a second one.
 */
import { Coins } from "lucide-react";

import { formatCredits } from "./plans";
import { useMonetization } from "./MonetizationProvider";

/** Below this, the pill tints — quietly, in the app's OWN accent. The number is the message; the
 *  colour is a nudge, and a nudge must not import a foreign palette to exist: amber said "warning"
 *  in a language the rest of the product does not speak (PO 2026-09-21: *"change the yellow glow to
 *  using the raisy theme please? no new colour here"*). `#0AB3BF` is the accent used 138 times across
 *  the app, in these same shapes: `text-[#0AB3BF]`, `bg-[#0AB3BF]/10`, `border-[#0AB3BF]/50`. No
 *  dark variant, matching every other use of it — it reads on both themes as it stands. */
const LOW_CREDITS = 5000;

export function CreditsCenter() {
  const { credits, open } = useMonetization();

  const isLow = typeof credits === "number" && credits < LOW_CREDITS;

  return (
    <div className="pointer-events-none fixed left-0 right-0 top-12 z-30 mx-auto w-fit">
      <button
        type="button"
        // pointer-events back on: the wrapper spans the full width to centre a `w-fit` child, and
        // without this it would swallow clicks across the top of the page.
        className={
          "pointer-events-auto flex items-center gap-2 rounded-b-xl border border-t-0 bg-card px-3.5 py-1.5 shadow-sm transition hover:bg-accent " +
          (isLow ? "border-[#0AB3BF]/60" : "border-border")
        }
        onClick={() => open("buy_credits")}
        title={
          typeof credits === "number"
              ? `${formatCredits(credits)} credits in your wallet — click to add credits`
            : "Reading your credit balance"
        }
      >
        <Coins
          className={"h-4 w-4 " + (isLow ? "text-[#0AB3BF]" : "text-muted-foreground")}
        />
        {typeof credits === "number" ? (
          <>
            <span className="text-sm font-semibold tabular-nums">
              {formatCredits(credits)}
            </span>
            <span className="text-xs text-muted-foreground">credits</span>
          </>
        ) : (
          // Not read yet — a placeholder, never a zero.
          <span className="h-3.5 w-16 animate-pulse rounded bg-muted" aria-label="Reading your credit balance" />
        )}
      </button>
    </div>
  );
}
